import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Button, Icon, Select } from 'components';
import Option from 'components/Select/Option';
import { theme, toRem } from 'design';
import { IPayPeriod } from 'types';
import { formatPayPeriodLabel, isPayPeriodClosed, payPeriodKey } from 'utils/payPeriods';

interface IPayPeriodOption {
  key: string;
  label: string;
  value: IPayPeriod;
}

// Not a span, which the Select would position absolutely over the label. The lock sits at the right, as on the rows.
const Selected = styled.div`
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
  white-space: nowrap;
`;

const Placeholder = styled.span`
  color: ${theme.color.neutral[400]};
`;

// The frame draws Show More as wide as the option rows.
const ShowMore = styled.div`
  padding: ${toRem(theme.space.xs)} ${toRem(theme.space.sm)} ${toRem(theme.space.md)};

  > button {
    width: 100%;
  }
`;

const Loading = styled.div`
  display: block;
  width: 100%;
`;

// Must not give up width to the label beside it.
const Lock = styled(Icon).attrs({ icon: 'LockClosed', width: 16, height: 16, fill: theme.color.neutral[800], dataCy: 'pay-period-lock', label: 'Closed' })`
  flex-shrink: 0;
`;

interface IPayPeriodSelectorProps {
  /** Newest first, as the store holds them. */
  payPeriods: IPayPeriod[];
  selected?: IPayPeriod | null;
  onChange: (payPeriod: IPayPeriod) => void;
  // Called with the oldest loaded period's start, the cursor for the next page.
  onLoadMore: (before: string) => void;
  // Skeleton until the first page arrives.
  isLoading?: boolean;
  isLoadingMore?: boolean;
  placeholder?: string;
  dataCy?: string;
}

// The Reports period picker: newest first, a lock on closed periods only, and Show More to load older ones.
const PayPeriodSelector = ({ payPeriods, selected, onChange, onLoadMore, isLoading = false, isLoadingMore = false, placeholder = 'Select payroll period', dataCy = 'periods-select' }: IPayPeriodSelectorProps) => {
  const options = useMemo<IPayPeriodOption[]>(() => payPeriods.map((payPeriod) => ({ key: payPeriodKey(payPeriod), label: formatPayPeriodLabel(payPeriod), value: payPeriod })), [payPeriods]);
  const selectedOption = selected ? options.find((option) => option.key === payPeriodKey(selected)) : undefined;
  const oldest = options[options.length - 1]?.value;

  const renderItem = ({ item, methods }: { item: IPayPeriodOption; methods: any }) => (
    <Option key={item.key} $selected={methods.isSelected(item)} onClick={() => methods.addItem(item)} data-cy="pay-period-option" data-value={item.key}>
      <div>
        <span>{item.label}</span>
        {isPayPeriodClosed(item.value) && <Lock />}
      </div>
    </Option>
  );

  const renderDropdown = ({ props, methods }: { props: { options: IPayPeriodOption[] }; methods: any }) => (
    <>
      {props.options.map((item) => renderItem({ item, methods }))}
      {oldest && (
        <ShowMore>
          <Button appearance="white" size="small" label="Show More" isProcessing={isLoadingMore} onClick={() => onLoadMore(oldest.start)} data-cy="pay-periods-show-more" />
        </ShowMore>
      )}
    </>
  );

  // From the picker's own selection, so a period that closes while selected shows its lock at once.
  const renderContent = () =>
    selectedOption ? (
      <Selected data-cy={`${dataCy}-selected`}>
        {selectedOption.label}
        {isPayPeriodClosed(selectedOption.value) && <Lock />}
      </Selected>
    ) : (
      <Placeholder>{placeholder}</Placeholder>
    );

  if (isLoading) {
    return (
      <Loading data-cy={`${dataCy}-loading`}>
        <Skeleton width="100%" height={theme.input.height.small} />
      </Loading>
    );
  }

  return (
    <Select
      dataCy={dataCy}
      placeholder={placeholder}
      options={options}
      values={selectedOption ? [selectedOption] : []}
      valueField="key"
      dropdownRenderer={renderDropdown}
      contentRenderer={renderContent}
      onChange={(selectedOptions: IPayPeriodOption[]) => {
        // Only a different period is a change; re-selecting the current one is not.
        const option = selectedOptions?.[0];
        if (option && (!selected || option.key !== payPeriodKey(selected))) onChange(option.value);
      }}
    />
  );
};

export default PayPeriodSelector;
